import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";
import Lightbox from "./Lightbox";
import { EASE } from "@/lib/site";

const QUOTES = [
  {
    id: "tangri",
    author: "Amit & Rashi Tangri",
    meta: "Apartment Renovation · Delhi NCR",
    paras: [
      "I am delighted to share my experience with Ms. Meenu Gaur who completed an interior renovation project at my home. Ma'am and her team demonstrated exceptional professionalism, skill, and dedication throughout the entire process. Everything was handled with remarkable attention to detail and care.",
      "She provided insightful recommendations that enhanced the overall design, and their craftsmanship was evident in every aspect of the work. The renovation was completed on schedule and within the agreed budget, which I greatly appreciate.",
      "I am thrilled with the results and have received numerous compliments on the transformation. I highly recommend Ms. Meenu Gaur and her team to anyone looking for exceptional interior work.",
    ],
  },
  {
    id: "terway",
    author: "Nupur & Sharad Terway",
    meta: "Two-Decade-Old Apartment · Delhi",
    paras: [
      "Meenu's ingenuity and knowledge were impressive, as the (Delhi) apartment was 2 decades old and presented some quirky challenges which we never could have imagined at purchase stage. She quickly overcame all challenges. Her close-knit team of trusted artisans worked steadily and collectively progressed at an impressive pace.",
      "The project neared fruition within anticipated timelines and we were thrilled with the results. Even today, we get a keen sense of satisfaction and tranquility in the ambience of our home. She has played very aesthetically with colors, combinations, contrasts, space, textures, lights and so much more in an understated but sophisticated manner.",
      "Entering our home is an automatic mood elevator, which we shall always be grateful for. Her strength is her passion for her work from which all other attributes flow; knowledge, experience, flair, style, creativity, discipline and so on.",
    ],
  },
];

const LETTERS = [
  {
    file: "ansal-2012-jan",
    org: "Ansal Housing & Construction Ltd.",
    note: "Appreciation letter — Karun Ansal, President (Projects), Jan 2012",
  },
  {
    file: "ansal-2012-oct",
    org: "Ansal Housing & Construction Ltd.",
    note: "Best project award recognition — Meerut Project, Oct 2012",
  },
  {
    file: "ongc-assam-asset",
    org: "ONGC — Assam Asset, Nazira",
    note: "Appreciation for interior guidance, Asset Manager's office, 2006",
  },
  {
    file: "ongc-golf-club",
    org: "ONGC Golf Club, Nazira",
    note: "Certificate — Golf Hut interiors & lawn decoration, 2006",
  },
];

const LETTER_IMAGES = LETTERS.map((l) => ({
  src: `/images/letters/${l.file}.jpg`,
  alt: `${l.org} — ${l.note}`,
}));

export default function Testimonials() {
  const [i, setI] = useState(0);
  const [lb, setLb] = useState(null);
  const q = QUOTES[i];

  return (
    <section id="testimonials" data-testid="testimonials-section" className="bg-ink text-cream py-24 lg:py-32 relative overflow-hidden">
      <div
        aria-hidden
        className="absolute -bottom-[6vw] -left-4 font-serif text-[20vw] leading-none text-white/[0.03] select-none pointer-events-none"
      >
        &ldquo;
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <FadeUp>
          <Eyebrow light>In their words</Eyebrow>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mt-5">
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-xl">
              Clients, architects &amp; developers
            </h2>
            <p className="text-sm text-cream/50 max-w-sm leading-relaxed">
              We have worked with various clients, other architects, designers, developers and many
              more — here is what a few of them wrote.
            </p>
          </div>
        </FadeUp>

        <div className="grid lg:grid-cols-12 gap-10 mt-14 items-start">
          <div className="lg:col-span-8 min-h-[360px] sm:min-h-[320px]">
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={q.id}
                data-testid={`testimonial-quote-${q.id}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="border-l border-brass/40 pl-6 sm:pl-9"
              >
                <Quote size={22} className="text-brass mb-6" />
                {q.paras.map((p, k) => (
                  <p
                    key={k}
                    className={`font-serif leading-relaxed text-cream/80 ${
                      k === 0 ? "text-xl lg:text-[1.6rem] text-cream/90" : "text-base lg:text-lg mt-5"
                    }`}
                  >
                    {p}
                  </p>
                ))}
                <footer className="mt-8">
                  <p className="font-display text-[11px] tracking-widest2 uppercase text-brass">
                    {q.author}
                  </p>
                  <p className="text-[12.5px] text-cream/45 mt-1.5">{q.meta}</p>
                </footer>
              </motion.blockquote>
            </AnimatePresence>

            <div className="flex items-center gap-4 mt-10 pl-6 sm:pl-9">
              <button
                data-testid="testimonial-prev"
                aria-label="Previous testimonial"
                onClick={() => setI((c) => (c - 1 + QUOTES.length) % QUOTES.length)}
                className="h-11 w-11 border border-cream/25 flex items-center justify-center hover:bg-brass hover:text-ink hover:border-brass transition-colors duration-300"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                data-testid="testimonial-next"
                aria-label="Next testimonial"
                onClick={() => setI((c) => (c + 1) % QUOTES.length)}
                className="h-11 w-11 border border-cream/25 flex items-center justify-center hover:bg-brass hover:text-ink hover:border-brass transition-colors duration-300"
              >
                <ChevronRight size={18} />
              </button>
              <span className="text-[11px] tracking-widest2 text-cream/40 font-display">
                {String(i + 1).padStart(2, "0")} / {String(QUOTES.length).padStart(2, "0")}
              </span>
            </div>
          </div>

          <FadeUp delay={0.1} className="lg:col-span-4">
            <div data-testid="appreciation-letters">
              <p className="font-display text-[10px] tracking-widest2 uppercase text-brass border-b border-white/10 pb-3">
                Letters of appreciation
              </p>
              <div className="grid grid-cols-2 gap-3 mt-5">
                {LETTERS.map((l, k) => (
                  <button
                    key={l.file}
                    data-testid={`letter-${l.file}`}
                    onClick={() => setLb({ images: LETTER_IMAGES, index: k })}
                    className="group relative overflow-hidden border border-white/10 hover:border-brass/50 transition-colors duration-300 cursor-zoom-in"
                  >
                    <img
                      src={`/images/letters/${l.file}.jpg`}
                      alt={`${l.org} — ${l.note}`}
                      loading="lazy"
                      className="w-full h-40 object-cover object-top opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                    />
                    <span className="block p-3 text-left text-[11px] leading-snug text-cream/55 group-hover:text-cream/80 transition-colors">
                      {l.org}
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-[11.5px] text-cream/35 mt-4 leading-relaxed">
                Original client &amp; corporate letters from ONGC and Ansal Housing — tap to read.
              </p>
            </div>
          </FadeUp>
        </div>
      </div>

      <Lightbox
        images={lb ? lb.images : []}
        index={lb ? lb.index : null}
        onClose={() => setLb(null)}
        onNavigate={(k) => setLb((cur) => (cur ? { ...cur, index: k } : cur))}
      />
    </section>
  );
}
